import React from "react";
import { subCategoryObject as category1Object } from "@/content/services/1";
import { categoryDetails as category1 } from "@/content/services/1";
import { categoryDetails as service2, subCategoryObject as category2Object } from "@/content/services/2";
import { categoryDetails as category2 } from "@/content/services/2";
import { BUSINESS_NAME, businessDetails, contactDetails } from "@/content";
import Link from "next/link";
import { ContentTitles, ParallaxHero, RegularHero, ServiceBlock, WhatWeDo } from "@modes0/ui-components";
import {PageSeo} from "@modes0/ui-components-seo";
import {seoSiteProfile} from "@/content/seo";
import {
    IconTiles,
    ProjectDisplay,
    ServiceTiles,
    ServicesHighlights,
    SimpleContact,
    type ProjectDisplayItem,
} from "@modes0/ui-components/client";
import {
    GENERAL_SERVICE_TERM_1,
    GENERAL_SERVICE_TERM_2,
    GENERAL_SERVICE_TERM_3,
    serviceBlockData,
} from "@/content/services/stand-alone-services";
import { parallaxContent1, parallaxContent2 } from "@/content/parallax-heros/parallax-heros.content";
import { whyUs } from "@/content/why-us/why-us";
import type { CategoryDetails } from "@/types";
import { featuredProjectDisplayItems } from "@/content/projects/project-display";
import ContactFormWidget from "@/widgets/contact-form/ContactFormWidget";

type ServicesHighlightsCategory = React.ComponentProps<typeof ServicesHighlights>["category"];
type ServiceTilesCategory = React.ComponentProps<typeof ServiceTiles>["service"];

const withServiceLinks = (category: CategoryDetails): CategoryDetails => ({
    ...category,
    subCategories: category.subCategories?.map((service) => ({
        ...service,
        link: service.link || `/${category.categoryType}/${category.slug}/${service.slug}`,
    })),
});

const primaryButtonClasses =
    "inline-flex items-center justify-center rounded-md bg-[#2faa2e] px-5 py-2.5 font-semibold text-white shadow-sm transition hover:bg-[#258d25]";
const secondaryButtonClasses =
    "inline-flex items-center justify-center rounded-md border border-[#2faa2e] bg-white px-5 py-2.5 font-semibold text-[#166534] transition hover:bg-white";
const contactAccentColor = "#2faa2e";
const contactAccentSoftColor = "#ffffff";
const contactColors = {
    accent: contactAccentColor,
    call: contactAccentColor,
    phone: contactAccentColor,
    sms: contactAccentColor,
    whatsapp: contactAccentColor,
    email: contactAccentColor,
    iconBackground: contactAccentSoftColor,
    supportIconBackground: contactAccentSoftColor,
};

const homepageProjectDisplayItems: ProjectDisplayItem[] = featuredProjectDisplayItems.map((project) => ({
    ...project,
    tags: [],
    services: [],
}));

export default function Home() {
    const backgroundStyles = {
        backgroundColor: "#ffffff",
    };

    const backgroundStyles2 = {
        backgroundColor: "#ffffff",
    };

    const backgroundStyles3 = {
        backgroundColor: "#ffffff",
    };

    const parallaxButtonLink1 = `/${category1Object.category1Service1.headCategory.categoryType}/${category1Object.category1Service1.headCategory.slug}/${category1Object.category1Service1.slug}`;
    const parallaxButtonLink2 = `/${category2Object.category2Service1.headCategory.categoryType}/${category2Object.category2Service1.headCategory.slug}/${category2Object.category2Service1.slug}`;
    const category1WithLinks = withServiceLinks(category1);
    const category2WithLinks = withServiceLinks(category2);

    return (
        <>
            <PageSeo siteProfile={seoSiteProfile} includeBusinessSchema />
            <div>
                <RegularHero
                    className="home-hero"
                    Link={Link}
                    image={{
                        src: parallaxContent1.imageUrl,
                        alt: "Business owner reviewing lead growth on a laptop",
                        decorative: true,
                        loading: "eager",
                        width: 1536,
                        height: 1024,
                    }}
                    eyebrow="Digital solutions that grow your business"
                    title={"More Leads.\nMore Customers."}
                    colors={{
                        lightOverlay: "transparent",
                    }}
                    titleHighlight="Real Growth."
                    lead="We build fast websites, run smart marketing campaigns, and set up systems that turn visitors into loyal customers."
                    primaryAction={{
                        label: "Grow Your Leads Now",
                        href: parallaxButtonLink1,
                        icon: false,
                    }}
                    secondaryAction={{
                        label: "Watch How It Works",
                        href: "#services",
                        icon: "play",
                        iconPosition: "start",
                        variant: "outline",
                    }}
                    trustItems={[
                        {
                            icon: false,
                            title: "5.0",
                            text: "Based on 100+ reviews",
                            stars: 5,
                        },
                        {
                            icon: "google",
                            title: "Google",
                            text: "Partner",
                        },
                        {
                            icon: "meta",
                            title: "Meta",
                            text: "Business Partner",
                        },
                    ]}
                    accentColor="#2eaa2d"
                    highlightColor="#2eaa2d"
                    backgroundColor="#ffffff"
                    maxWidth="100%"
                />

                <div id="services" style={{ scrollMarginTop: 88 }}>
                    <WhatWeDo
                        Link={Link}
                        eyebrow="What We Do"
                        title={
                            <>
                                Digital Solutions
                                <br />
                                That Deliver Results
                            </>
                        }
                        points={[
                            "Website Design & Development",
                            "Google Ads & Social Media Ads",
                            "SEO & Local Search",
                            "Lead Generation & Automation",
                            "Analytics & Conversion Tracking",
                        ]}
                        pointIcon="check"
                        cta={{
                            label: "Explore All Services",
                            href: "/services",
                            icon: <span style={{ display: "none" }} />,
                        }}
                        image={{
                            src: "/images/services/what-we-do/growleads.webp",
                            alt: "Grow Leads digital marketing and website services displayed on laptop and mobile screens",
                            width: 1536,
                            height: 1024,
                        }}
                        badges={[
                            {
                                title: "Projects Delivered",
                                value: "250+",
                                icon: "rocket",
                            },
                            {
                                title: "Client Revenue Growth",
                                value: "300%+",
                                icon: "dollar",
                            },
                        ]}
                        accentColor="#22b428"
                        accentDarkColor="#178a1d"
                    />
                </div>

                <ProjectDisplay
                    Link={Link}
                    items={homepageProjectDisplayItems}
                    layout="slide"
                    className="home-project-display"
                    eyebrow="Our Work"
                    title={
                        <>
                            Work That <span className="project-display-title-highlight">Wins Clients.</span>
                        </>
                    }
                    description="Real results for real businesses. Explore how we help brands attract more leads, convert visitors and grow with confidence."
                    actionHref="/projects"
                    actionLabel="View all projects"
                    actionAriaLabel="View all Grow Leads projects"
                    actionIconToken="play"
                    showFilters={false}
                    ctaLabel="View Project"
                    accentColor="#2faa2e"
                    backgroundColor="#ffffff"
                    surfaceColor="#ffffff"
                    colors={{
                        sectionBorder: "rgba(16, 24, 40, 0.1)",
                    }}
                    maxWidth={1440}
                    tileWidth={212}
                    minTileWidth={216}
                    imageHeight={190}
                    ariaLabel="Featured Grow Leads client projects"
                    style={{ padding: "64px 16px" }}
                />

                <ServicesHighlights
                    category={category1WithLinks as ServicesHighlightsCategory}
                    LinkComponent={Link}
                    alternate
                    showMainCategoryLink
                    backgroundColor="#ffffff"
                    panelBackgroundColor="#ffffff"
                />

                <div className="py-10 bg-white" style={backgroundStyles3}>
                    <ContentTitles
                        title={`Why ${BUSINESS_NAME}?`}
                        subTitle={`Six reasons to use our ${GENERAL_SERVICE_TERM_3}`}
                        subTitleComponent="h3"
                        titleComponent="h4"
                    />
                    <IconTiles content={whyUs} />
                    <div className="py-4 text-center">
                        <Link href="/about" className={secondaryButtonClasses}>
                            More about {BUSINESS_NAME}
                        </Link>
                    </div>
                </div>

                <ParallaxHero
                    title={parallaxContent2.title}
                    subTitle={parallaxContent2.subTitle}
                    overline={parallaxContent2.overline}
                    imageUrl={parallaxContent2.imageUrl}
                    buttonCTA={parallaxContent2.buttonCTA}
                    buttonLink={parallaxButtonLink2}
                    tint={parallaxContent2.tint}
                    align={parallaxContent2.align === "center" ? "left" : parallaxContent2.align}
                />

                <ServicesHighlights
                    category={category2WithLinks as ServicesHighlightsCategory}
                    LinkComponent={Link}
                    alternate
                    showMainCategoryLink
                    backgroundColor="#ffffff"
                    panelBackgroundColor="#ffffff"
                />

                <div className="bg-white" style={backgroundStyles2}>
                    <ServiceBlock
                        data={serviceBlockData}
                        eyebrow="Grow Leads services"
                        title="Website Design, SEO & Lead Generation Services"
                        description={`Easy-to-understand digital marketing services for ${
                            businessDetails?.generalServiceArea || "Sydney"
                        } businesses that want more local customers, more enquiries and clearer online growth.`}
                        className="grow-leads-service-block"
                        backgroundColor="#ffffff"
                        maxWidth={1180}
                        imageHeight={250}
                        imageHeightMobile={210}
                        ariaLabel="Grow Leads website design, SEO and lead generation services"
                    />

                    <div className="m-auto pb-10 text-center">
                        <Link href="/contact" className={primaryButtonClasses}>
                            Contact - {BUSINESS_NAME}
                        </Link>
                    </div>
                </div>

                <div className="py-12 bg-white">
                    <ContentTitles
                        title={category1.name}
                        subTitle={`${businessDetails?.generalServiceArea} ${GENERAL_SERVICE_TERM_1} Services`}
                        subTitleComponent="h3"
                        titleComponent="h4"
                    />

                    <ServiceTiles
                        service={category1WithLinks as ServiceTilesCategory}
                        Link={Link}
                        backgroundColor="#ffffff"
                        panelBackgroundColor="#ffffff"
                    />
                    <div className="m-auto text-center">
                        <Link href="/contact" className={secondaryButtonClasses}>
                            Contact - {BUSINESS_NAME}
                        </Link>
                    </div>
                </div>

                <div className="py-10 bg-white">
                    <ContentTitles
                        title={category2.name}
                        subTitle={`${businessDetails?.generalServiceArea} ${GENERAL_SERVICE_TERM_2} Services`}
                        subTitleComponent="h3"
                        titleComponent="h4"
                    />

                    <ServiceTiles
                        service={category2WithLinks as ServiceTilesCategory}
                        Link={Link}
                        backgroundColor="#ffffff"
                        panelBackgroundColor="#ffffff"
                    />
                </div>

                <div className="py-10" style={backgroundStyles}>
                    <div className="bg-white pt-4 pb-8 px-3 max-w-5xl mx-auto">
                        <ContentTitles title="Contact Us" scrambleTitle overline={`${BUSINESS_NAME}`} />

                        <SimpleContact
                            contactDetails={contactDetails}
                            businessName={BUSINESS_NAME}
                            serviceName={service2.name}
                            pageUrl="/contact"
                            accentColor={contactAccentColor}
                            colors={contactColors}
                            labels={{
                                supportTitle: `Need more information on ${service2.name} services?`,
                                supportDescription: "Contact us with your enquiries",
                            }}
                        />
                    </div>
                </div>

                <ContactFormWidget />
            </div>
        </>
    );
}
